import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, Gauge, Code2, Globe, BarChart, Shield } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const TechnicalSEOPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Technical SEO Services | Consiliuma - Nottingham SEO Experts</title>
        <meta name="description" content="Expert technical SEO services in Nottingham. Improve your website's search engine visibility, performance, and rankings with our comprehensive technical optimization." />
        <meta name="keywords" content="Technical SEO Nottingham, SEO Optimization, Website Performance, Search Engine Optimization, SEO Services UK" />
        <link rel="canonical" href="/services/technical-seo" />
      </Helmet>

      <HeroSection
        title="Technical SEO Services"
        subtitle="Optimize your website's technical foundation for better search rankings and visibility"
        ctaText="Get a Free SEO Audit"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/196646/pexels-photo-196646.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Comprehensive Technical SEO Solutions"
            subtitle="Optimizing every technical aspect of your website for search engines"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Search size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Site Structure Optimization</h3>
              <p className="text-secondary">
                Improving your website's architecture and internal linking for better crawling and indexing.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Gauge size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Performance Optimization</h3>
              <p className="text-secondary">
                Enhancing page speed and Core Web Vitals for better user experience and rankings.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Code2 size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Schema Markup</h3>
              <p className="text-secondary">
                Implementing structured data to help search engines better understand your content.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Globe size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Mobile Optimization</h3>
              <p className="text-secondary">
                Ensuring your website performs flawlessly on all devices for mobile-first indexing.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <BarChart size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Analytics & Reporting</h3>
              <p className="text-secondary">
                Detailed tracking and analysis of your website's search performance and rankings.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Security & SSL</h3>
              <p className="text-secondary">
                Implementing security measures that boost search rankings and user trust.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Our Technical SEO Process"
                subtitle="A systematic approach to improving your search visibility"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Technical Audit</h3>
                    <p className="text-secondary">
                      Comprehensive analysis of your website's technical health and search performance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Strategy Development</h3>
                    <p className="text-secondary">
                      Creating a tailored optimization plan based on audit findings and your goals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Implementation</h3>
                    <p className="text-secondary">
                      Executing technical improvements and optimizations across your website.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Monitoring</h3>
                    <p className="text-secondary">
                      Continuous tracking of rankings, traffic, and technical performance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Refinement</h3>
                    <p className="text-secondary">
                      Ongoing optimization based on performance data and search engine updates.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button to="/quote" variant="primary">
                  Get Your SEO Audit
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/196647/pexels-photo-196647.jpeg"
                alt="Technical SEO process"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Benefits of Technical SEO"
            subtitle="How technical optimization improves your online presence"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Better Rankings</h3>
              <p className="text-secondary">
                Improved search engine rankings through optimized technical foundation and structure.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">More Traffic</h3>
              <p className="text-secondary">
                Increased organic search traffic from better visibility and click-through rates.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Better User Experience</h3>
              <p className="text-secondary">
                Enhanced user experience through faster loading times and better site structure.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Mobile Performance</h3>
              <p className="text-secondary">
                Optimized mobile experience for better rankings in mobile search results.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Competitive Edge</h3>
              <p className="text-secondary">
                Stand out from competitors with superior technical optimization.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">ROI</h3>
              <p className="text-secondary">
                Better conversion rates through improved user experience and site performance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Improve Your Search Rankings?"
        subtitle="Get a free technical SEO audit and discover your website's potential"
        buttonText="Get Started"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default TechnicalSEOPage;